import { useState, useEffect } from 'react';
import './ClientLogin.css';
import PasswordInput from './PasswordInput';

const API = '/api';

const Icon = ({ d, size = 20, color = 'currentColor', className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    lock: ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4'],
    arrow: 'M5 12h14M12 5l7 7-7 7',
    check: 'M20 6L9 17l-5-5',
    back: 'M19 12H5M12 19l-7-7 7-7',
};

export default function ClientLogin({ onLogin }) {
    const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgotEmail, setForgotEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [branding, setBranding] = useState(null);
    const [brandingLoading, setBrandingLoading] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');
        const opId = params.get('op');
        if (opId) {
            fetch(`${API}/operators/${opId}`)
                .then(res => res.json())
                .then(data => { if (data && data.companyName) setBranding(data); })
                .catch(err => console.error("Error fetching branding:", err));
        }
    }, []);

    const handleEmailBlur = async () => {
        if (!email || !email.includes('@') || branding) return;
        setBrandingLoading(true);
        try {
            const res = await fetch(`${API}/client-branding?email=${encodeURIComponent(email)}`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.companyName) setBranding(data);
            }
        } catch (err) { /* silently fail */ }
        finally { setBrandingLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/client-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Login failed');
            localStorage.setItem('ops-user', JSON.stringify(data));
            onLogin(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/client-forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail }),
            });
            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Server is unavailable. Please try again in a moment.');
            }
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to send reset email. Please try again.');
            setView('forgot-sent');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-side-decoration client-theme">
                <div className="decor-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        {branding?.companyLogo ? (
                            <img
                                src={`${API}/uploads/${branding.companyLogo}`}
                                alt="Logo"
                                style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover' }}
                            />
                        ) : (
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#00B2A9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '24px' }}>
                                {branding?.companyName ? branding.companyName.charAt(0) : 'C'}
                            </div>
                        )}
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>{branding?.companyName || 'Client Portal'}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Passenger Management</div>
                        </div>
                    </div>
                    <h2>Welcome to the Client Portal</h2>
                    <p>Book trips, manage invoices, and track your passenger transportation seamlessly.</p>
                </div>
                <div className="animated-blobs">
                    <div className="blob blob-1"></div>
                    <div className="blob blob-2"></div>
                    <div className="blob blob-3"></div>
                </div>
            </div>

            <div className="login-main">
                <div className="login-box">
                    
                    {view === 'login' && (
                        <>
                            <div className="login-box-header">
                                <h1>Welcome Back</h1>
                                <p>{branding ? `Sign in to your ${branding.companyName} Client Panel` : 'Sign in to your Client Control Panel'}</p>
                            </div>

                            <form className="login-form-premium" onSubmit={handleSubmit}>
                                {error && (
                                    <div className="error-message-shake">
                                        <Icon d={['M12 8v4', 'M12 16h.01', 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z']} size={18} />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="input-field">
                                    <label>Email Address</label>
                                    <div className="input-inner">
                                        <Icon d={icons.mail} size={18} className="input-icon-svg" />
                                        <input
                                            type="email"
                                            placeholder="client@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={handleEmailBlur}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="input-field">
                                    <div className="label-row">
                                        <label>Password</label>
                                        <button 
                                            type="button" 
                                            onClick={() => { setView('forgot'); setError(''); setForgotEmail(email); }}
                                            className="forgot-link" 
                                            style={{ color: '#00B2A9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                    <div className="input-inner">
                                        <Icon d={icons.lock} size={18} className="input-icon-svg" />
                                        <PasswordInput
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <span className="loader-text">
                                            <span className="dot-flashing"></span>
                                            Authenticating...
                                        </span>
                                    ) : (
                                        <>
                                            <span>Sign In</span>
                                            <Icon d={icons.arrow} size={18} strokeWidth={2} />
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="login-box-footer">
                                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <a href="#operator-login" className="forgot-link" style={{ color: '#063A5C', fontWeight: 600 }}>→ Login as Operator Dashboard</a>
                                </div>
                            </div>
                        </>
                    )}

                    {view === 'forgot' && (
                        <>
                            <div className="login-box-header">
                                <h1>Reset Password</h1>
                                <p>Enter your registered email address</p>
                            </div>

                            <form className="login-form-premium" onSubmit={handleForgotSubmit}>
                                {error && (
                                    <div className="error-message-shake">
                                        <Icon d={['M12 8v4', 'M12 16h.01', 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z']} size={18} />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="input-field">
                                    <label>Email Address</label>
                                    <div className="input-inner">
                                        <Icon d={icons.mail} size={18} className="input-icon-svg" />
                                        <input
                                            type="email"
                                            placeholder="your@company.com"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="submit-btn" disabled={loading}>
                                    {loading ? (
                                        <span className="loader-text">
                                            <span className="dot-flashing"></span>
                                            Sending Reset Link...
                                        </span>
                                    ) : (
                                        <>
                                            <span>Send Reset Link</span>
                                            <Icon d={icons.arrow} size={18} strokeWidth={2} />
                                        </>
                                    )}
                                </button>
                            </form>
                            
                            <div className="login-box-footer" style={{ marginTop: 24, textAlign: 'center' }}>
                                <button 
                                    onClick={() => { setView('login'); setError(''); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#00B2A9', fontSize: 13, fontWeight: 700, margin: '0 auto' }}
                                >
                                    <Icon d={icons.back} size={15} /> Back to Sign In
                                </button>
                            </div>
                        </>
                    )}

                    {view === 'forgot-sent' && (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%',
                                background: '#E6F7F6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 20px',
                            }}>
                                <Icon d={icons.check} size={28} color="#00B2A9" />
                            </div>
                            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', margin: '0 0 10px' }}>Check Your Email</h2>
                            <p style={{ fontSize: 16, color: '#64748b', lineHeight: 1.6, margin: '0 0 8px' }}>
                                We've sent a password reset link to
                            </p>
                            <p style={{ fontSize: 16, fontWeight: 700, color: '#00B2A9', margin: '0 0 24px' }}>{forgotEmail}</p>
                            <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6, margin: '0 0 28px' }}>
                                Click the link in the email to reset your password. The link will expire in <strong>1 hour</strong>.<br />
                                Check your spam folder if you don't see it.
                            </p>
                            <button
                                onClick={() => { setView('login'); setError(''); setForgotEmail(''); }}
                                className="submit-btn"
                            >
                                Back to Login
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
